import React, { useEffect, useState, forwardRef, useMemo } from 'react';
import { IconWrapper } from '@flexo/general';
import styles from './textfield.module.scss';
import { TextfieldProps } from '@flexo/atoms';

// Constants
const ENV = (import.meta as any).env;
const IS_WEB = ENV?.VITE_APP_IS_WEB === 'true';
const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const IS_WEB_SAFARI = IS_WEB && IS_SAFARI;

// Typography constants
const TYPOGRAPHY = {
  heading4: IS_WEB_SAFARI ? 'heading4Safari' : 'heading4',
  heading4L: IS_WEB_SAFARI ? 'heading4Safari' : 'heading4L',
  heading5: 'heading5',
  detail: 'detail',
  paragraph: 'paragraph',
} as const;

// Component variants
const TEXTFIELD_VARIANTS = {
  default: 'default',
  containedLabel: 'containedLabel',
} as const;

type TextfieldVariant = keyof typeof TEXTFIELD_VARIANTS;

// Types for className builders
interface ClassNameBuilderConfig {
  base: string;
  conditions: Array<{
    condition: boolean;
    className: string;
  }>;
  typography?: keyof typeof TYPOGRAPHY;
}

interface LabelClassConfig {
  error: boolean | null;
  variant: TextfieldVariant;
  isActiveOrHasValue?: boolean;
  disabled?: boolean;
}

interface InputContainerClassConfig {
  variant: TextfieldVariant;
  styleVariant: string;
  border: boolean;
  error: boolean;
  disabled: boolean;
  color: string;
}

interface InputClassConfig {
  variant: TextfieldVariant;
  disabled: boolean;
}

interface DescriptionClassConfig {
  styleVariant: string;
  hasClickHandler: boolean;
}

// Central className builder utility
const buildClassName = ({
  base,
  conditions,
  typography,
}: ClassNameBuilderConfig): string => {
  const classes = [
    base,
    typography ? TYPOGRAPHY[typography] : '',
    ...conditions
      .filter(({ condition }) => condition)
      .map(({ className }) => className),
  ];

  return classes.filter(Boolean).join(' ');
};

// Updated TextfieldProps interface
interface UpdatedTextfieldProps extends Omit<TextfieldProps, 'variant'> {
  variant?: TextfieldVariant;
  styleVariant?: string;
}

// Use forwardRef to properly pass ref from parent component to input
export const Textfield = forwardRef<HTMLInputElement, UpdatedTextfieldProps>(
  (
    {
      variant = 'default',
      placeholder,
      label,
      onChange,
      description,
      color = 'grey',
      icon,
      iconPosition,
      onIconClick,
      onDescriptionClick,
      styleVariant = 'primary',
      extraDescription,
      border = false,
      error = false,
      errorDescription,
      disabled = false,
      additionalStyles = null,
      ...props
    }: UpdatedTextfieldProps,
    ref
  ) => {

    const [isFocused, setIsFocused] = useState(false);
    const [isActiveOrHasValue, setActiveOrHasValue] = useState(
      Boolean(props?.value)
    );

    // Computed values
    const isContainedLabel = variant === 'containedLabel';
    const isDefaultVariant = variant === 'default';

    // Memoized className builders
    const classNames = useMemo(() => {
      // Build label classes
      const buildLabelClasses = (config: LabelClassConfig): string => {
        const baseClass = config.error
          ? styles.Textfield__Label__Error
          : styles.Textfield__Label;

        if (config.variant === 'containedLabel') {
          return buildClassName({
            base: baseClass,
            typography: 'heading4L',
            conditions: [
              {
                condition: config.isActiveOrHasValue || false,
                className: `${TYPOGRAPHY.detail} ${styles.activeLabel}`,
              },
              {
                condition: true,
                className: styles.Textfield__Label__Contained,
              },
              {
                condition: config.disabled || false,
                className: styles.Textfield__Label__Contained__Disabled,
              },
            ],
          });
        }

        // Default variant label
        return buildClassName({
          base: baseClass,
          typography: 'heading4',
          conditions: [],
        });
      };

      // Build input container classes
      const buildInputContainerClasses = (
        config: InputContainerClassConfig
      ): string => {
        return buildClassName({
          base: styles.Textfield__InputContainer,
          typography: 'heading5',
          conditions: [
            { condition: true, className: styles[config.styleVariant] },
            { condition: config.border, className: styles.border },
            {
              condition: config.variant === 'containedLabel',
              className: styles.containedLabel,
            },
            {
              condition: config.error && config.variant === 'containedLabel',
              className: styles.error,
            },
            {
              condition: config.error && config.variant === 'default',
              className: styles.Textfield__InputContainer__Error,
            },
            {
              condition: config.disabled && config.variant === 'containedLabel',
              className: styles.containedLabel__Disabled,
            },
            {
              condition: config.color === 'grey',
              className: styles.greyBackground,
            },
            {
              condition: config.variant === 'default' && config.disabled,
              className: styles.Textfield__InputContainer__Disabled,
            },
          ],
        });
      };

      // Build input classes
      const buildInputClasses = (config: InputClassConfig): string => {
        return buildClassName({
          base: styles.Textfield__Input,
          conditions: [
            { condition: IS_WEB, className: styles.Textfield__Input__Web },
            {
              condition: config.variant === 'containedLabel',
              className: styles.Textfield__Input__Contained,
            },
            {
              condition: config.disabled,
              className: styles.Textfield__Input__Contained__Disabled,
            },
          ],
        });
      };

      // Build description classes
      const buildDescriptionClasses = (
        config: DescriptionClassConfig
      ): string => {
        return buildClassName({
          base: styles.Textfield__Description,
          typography: 'paragraph',
          conditions: [
            {
              condition: true,
              className:
                styles[`Textfield__Description__${config.styleVariant}`],
            },
            {
              condition: config.hasClickHandler,
              className: styles.Textfield__Description__Clickable,
            },
          ],
        });
      };

      return {
        inputContainer: buildInputContainerClasses({
          variant,
          styleVariant,
          border,
          error,
          disabled,
          color,
        }),
        input: buildInputClasses({ variant, disabled }),
        label: buildLabelClasses({
          variant,
          isActiveOrHasValue,
          disabled,
          error,
        }),
        standardLabel: buildLabelClasses({
          variant: 'default',
          disabled,
          error,
        }),
        description: buildDescriptionClasses({
          styleVariant,
          hasClickHandler: Boolean(onDescriptionClick),
        }),
      };
    }, [
      error,
      variant,
      styleVariant,
      border,
      disabled,
      color,
      isActiveOrHasValue,
      onDescriptionClick,
    ]);

    // Event handlers
    const handleBlur = () => {
      setIsFocused(false);
      if (!props.value) {
        setActiveOrHasValue(false);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setActiveOrHasValue(true);
    };

    const handleIconClick = () => {
      onIconClick?.(props.name);
    };

    // Sync with props.value changes
    useEffect(() => {
      setActiveOrHasValue(Boolean(props.value) || isFocused);
    }, [props.value, isFocused]);

    // Build input props
    const inputProps = {
      key: props.name,
      ref,
      className: classNames.input,
      placeholder: isDefaultVariant ? placeholder : '',
      onChange,
      autoCapitalize: 'none' as const,
      disabled,
      ...(props?.onKeyUp && { onKeyUp: props.onKeyUp }),
      ...props,
    };

    return (
      <div
        className={styles.Textfield}
        style={additionalStyles || {}}
        onBlur={handleBlur}
        onFocus={handleFocus}
      >
        {/* Standard Label (default variant) */}
        {label && isDefaultVariant && (
          <span className={classNames.standardLabel}>{label}</span>
        )}

        {/* Input Container */}
        <div className={classNames.inputContainer}>
          {/* Contained Label */}
          {label && isContainedLabel && (
            <div className={classNames.label}>{label}</div>
          )}

          {/* Input Field */}
          <input {...inputProps} />

          {/* Icon */}
          {icon && (
            <div
              className={styles.Textfield__Icon}
              onMouseDown={handleIconClick}
            >
              <IconWrapper iconName={icon} />
            </div>
          )}
        </div>

        {/* Description Container */}
        <div className={styles.Textfield__Description__Container}>
          {/* Main Description */}
          {description && (
            <span
              className={classNames.description}
              onClick={onDescriptionClick}
            >
              {description}
            </span>
          )}

          {/* Error Description */}
          {errorDescription && (
            <span
              className={`${styles.Textfield__Error__Description} ${TYPOGRAPHY.detail}`}
              onClick={onDescriptionClick}
            >
              {errorDescription}
            </span>
          )}

          {/* Extra Description */}
          {extraDescription && (
            <span
              className={`${TYPOGRAPHY.detail} ${styles.Textfield__ExtraDescription}`}
            >
              {extraDescription}
            </span>
          )}
        </div>
      </div>
    );
  }
);

// Set display name for better debugging
Textfield.displayName = 'Textfield';

export default Textfield;
